import { useState, useEffect } from "react"
import { Menu, X, Globe, Clock } from "lucide-react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/je-button"
import { useLanguage } from "@/contexts/LanguageContext"

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { language, setLanguage, t } = useLanguage()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { href: "/#stats", label: t('nav.stats') },
    { href: "/#services", label: t('nav.services') },
    { href: "/#process", label: t('nav.process') },
    { href: "/#projects", label: t('nav.projects') },
    { href: "/#faq", label: t('nav.faq') },
    { href: "/#contact", label: t('nav.contact') }
  ]

  const scrollToSection = (href: string) => {
    const id = href.split('#')[1];
    if (id) {
      const element = document.getElementById(id);
      if (element) {
        const headerHeight = 80; // Account for sticky header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      // Si nous ne sommes pas sur la page d'accueil, naviguer d'abord vers l'accueil
      if (window.location.pathname !== '/') {
        window.location.href = href;
        return;
      }
      // Si nous sommes déjà sur la page d'accueil, faire un scroll normal
      scrollToSection(href);
      setIsOpen(false);
    }
  }

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 nav-modern ${isScrolled ? 'scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl font-black text-je-black hover:text-je-red transition-colors duration-300">
                JE <span className="text-gradient">HEPIA</span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:block">
              <div className="ml-12 flex items-baseline space-x-10">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-je-black hover:text-je-red transition-all duration-300 font-medium relative group"
                  >
                    {link.label}
                    <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-je-red transition-all duration-300 group-hover:w-full"></span>
                  </a>
                ))}
              </div>
            </div>

            {/* Language Toggle & Auth Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => setLanguage(language === 'fr' ? 'en' : language === 'en' ? 'de' : 'fr')}
                className="px-3 py-2 rounded-md text-je-black hover:text-je-red transition-colors duration-300 flex items-center gap-2"
                aria-label="Switch language"
              >
                <Globe size={20} />
                <span className="text-sm font-medium">{language.toUpperCase()}</span>
              </button>
              
              {/* Auth Button */}
              <Link to="/auth">
                <Button variant="outline" size="sm" className="border-je-red text-je-red hover:bg-je-red hover:text-white px-4 py-2">
                  <Clock className="w-4 h-4 mr-2" />
                  Connexion
                </Button>
              </Link>
              
              <Button size="md" className="btn-modern px-6 py-2" asChild>
                <a href="/#contact">{t('nav.launchProject')}</a>
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-3 rounded-md text-je-black hover:text-je-red transition-colors duration-300"
                aria-label="Menu mobile"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className={`mobile-menu ${isOpen ? 'open' : ''}`}>
        <div className="flex flex-col items-center justify-center h-full space-y-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-black text-je-black">
              JE <span className="text-gradient">HEPIA</span>
            </h1>
          </div>
          
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-xl font-medium text-je-black hover:text-je-red transition-colors duration-300"
              onClick={(e) => handleNavClick(e, link.href)}
            >
              {link.label}
            </a>
          ))}
          
          <div className="flex flex-col items-center gap-4 pt-8">
            {/* Auth Button Mobile */}
            <div className="flex flex-col gap-3 w-full max-w-xs">
              <Link to="/auth" onClick={() => setIsOpen(false)}>
                <Button className="w-full bg-je-red text-white hover:bg-je-red-dark">
                  <Clock className="w-4 h-4 mr-2" />
                  Connexion
                </Button>
              </Link>
            </div>
            
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : language === 'en' ? 'de' : 'fr')}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-je-black hover:text-je-red transition-colors duration-300"
              aria-label="Switch language"
            >
              <Globe size={18} />
              <span className="font-medium">{language.toUpperCase()}</span>
            </button>
            
            <Button size="lg" className="btn-modern" onClick={() => setIsOpen(false)} asChild>
              <a href="/#contact">{t('nav.launchProject')}</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-je-black/20 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  )
}

export default Navigation